// ============================================================================
// Feuille du noyau : ÉQUATIONS DIFFÉRENTIELLES (refonte, lot 6.67, moteur v62).
// ============================================================================
// L'énoncé : une équation différentielle linéaire d'ordre 1 ou 2, « y′ = −2y + 8 »,
// « y″ − 3y′ + 2y = 0 », avec ou sans conditions initiales (« y(0) = 1 », « y′(0) = 0 »).
// La question demande soit l'équation réécrite (« y′ = −y ÷ 2 + 2 »), soit toutes les
// solutions (« y = C × e^(−2x) + 4 »), soit la solution qui vérifie les conditions, soit
// une solution. L'élève écrit, une étape par ligne :
//
//   « y′ = −y ÷ 2 + 2 »             réécrire l'équation (isoler y′)           1
//   « y′ = −2y »                    l'équation homogène                       1
//   « r² − 3r + 2 = 0 »             l'équation caractéristique (ordre 2)      1
//   « Δ = 1 »                       son discriminant                          1
//   « r = 1 ou r = 2 »              ses racines (complexes : « −1 + 2i »)     1
//   « y = C × e^(−2x) »             les solutions de l'équation homogène      2
//   « y = 4 »                       une solution constante                    1
//   « y = C × e^(−2x) + 4 »         toutes les solutions                      2
//                                   (homogène + particulière : 1)
//   « y′ = −A sin(x) + B cos(x) »   dériver les solutions                     1
//   « y(0) = C + 4 »                remplacer x par 0                          1
//   « C + 4 = 1 »                   utiliser la condition y(0) = 1            1
//   « C = −3 »                      trouver C                                 1
//   « y = −3 × e^(−2x) + 4 »        remplacer C                               1
//
// « y′ », « y″ » (aussi « y' », « y′′ », « y'' ») sont des bouts ; une constante (« C »,
// « A », « B », « C₁ »…) est une lettre déclarée par le modèle (`kernel.ode.constants`).
// Les vérités se calculent en flottants : une fonction est solution si y, y′, y″ calculés
// (dérivée exacte) vérifient l'équation en 11 points de l'intervalle (preuve
// ÉCHANTILLONNÉE, comme les dérivées du lot 5.9) ; une famille est « toutes les
// solutions » si elle est affine en ses constantes, que chacune de ses fonctions est
// solution et qu'elle a autant de solutions indépendantes que l'ordre. Une ligne fausse ne
// dit jamais la bonne réponse ; une ligne juste qui saute des étapes reste sur la feuille
// sans HORA (D7).
//
// Moteur v63 (lot 6.68) : la variable peut être t (`kernel.ode.variable`), et la question
// peut demander la VALEUR de la solution en un point (réponse « f(0) = 5 × e^(−2) ») :
//   « f(0) = C »                    remplacer x par 0 dans les solutions        1
//   « f(0) = 5 × e^(−2) »           la valeur, une fois la solution trouvée     1
// La valeur attendue est calculée par une intégration numérique (Runge-Kutta 4) depuis les
// conditions, indépendante du corrigé.
// ============================================================================

import type { Chip } from './lexer';
import { isNum, lex, normalize } from './lexer';
import type { ScratchModel } from './model';
import { atomsText, parseLine, type Line, type Node } from './tree';
import {
  add,
  canon,
  fromNode,
  mul,
  num,
  substitute,
  symbolsOf,
  termKey,
  termText,
  type Term,
} from './kernel/term';
import { evalFloat } from './kernel/normal';
import { derive } from './kernel/calculus';

export const ODE_STEP_IDS = {
  rewrite: 'ode.rewrite@1',
  homogeneous: 'ode.homogeneous@1',
  characteristic: 'ode.characteristic@1',
  discriminant: 'ode.discriminant@1',
  roots: 'ode.roots@1',
  general: 'ode.general@1',
  homogSolutions: 'ode.homogeneous_solutions@1',
  constant: 'ode.constant@1',
  particular: 'ode.particular@1',
  superpose: 'ode.superpose@1',
  derive: 'ode.derive@1',
  evaluate: 'ode.evaluate@1',
  condition: 'ode.condition@1',
  solveC: 'ode.solve_constant@1',
  member: 'ode.member@1',
  /** moteur v63 : la valeur de la solution en un point */
  value: 'ode.value@1',
  compute: 'kernel.compute@1',
} as const;
type StepKind = keyof typeof ODE_STEP_IDS;

const STEP: Record<StepKind, { label: string; hora: number; why: string }> = {
  rewrite: {
    label: 'Transformer l’équation',
    hora: 1,
    why: 'Une équation équivalente : on passe un terme de l’autre côté, on divise par un nombre non nul.',
  },
  homogeneous: {
    label: 'Équation homogène',
    hora: 1,
    why: 'L’équation homogène (sans second membre) : on remplace le second membre par 0.',
  },
  characteristic: {
    label: 'Équation caractéristique',
    hora: 1,
    why: 'À ay″ + by′ + cy = 0 correspond l’équation caractéristique ar² + br + c = 0.',
  },
  discriminant: {
    label: 'Discriminant',
    hora: 1,
    why: 'Le discriminant de ar² + br + c : Δ = b² − 4ac.',
  },
  roots: {
    label: 'Racines',
    hora: 1,
    why: 'Les racines de l’équation caractéristique donnent les solutions.',
  },
  general: {
    label: 'Toutes les solutions',
    hora: 2,
    why: 'Toutes les solutions de l’équation.',
  },
  homogSolutions: {
    label: 'Solutions de l’équation homogène',
    hora: 2,
    why: 'Les solutions de l’équation homogène.',
  },
  constant: {
    label: 'Solution constante',
    hora: 1,
    why: 'Une fonction constante y = k : sa dérivée est nulle, il reste une équation en k.',
  },
  particular: {
    label: 'Solution particulière',
    hora: 1,
    why: 'Une solution de l’équation complète : en la remplaçant dans l’équation, on retrouve l’égalité.',
  },
  superpose: {
    label: 'Toutes les solutions',
    hora: 1,
    why: 'Toutes les solutions = les solutions de l’équation homogène + une solution particulière.',
  },
  derive: {
    label: 'Dériver',
    hora: 1,
    why: 'On dérive les solutions : les constantes restent des constantes.',
  },
  evaluate: {
    label: 'Calculer',
    hora: 1,
    why: 'On remplace x par sa valeur dans les solutions.',
  },
  condition: {
    label: 'Utiliser la condition',
    hora: 1,
    why: 'La condition donne une équation en la constante.',
  },
  solveC: {
    label: 'Trouver la constante',
    hora: 1,
    why: 'On résout l’équation en la constante.',
  },
  member: {
    label: 'Remplacer la constante',
    hora: 1,
    why: 'On remplace la constante par sa valeur dans les solutions.',
  },
  value: {
    label: 'Calculer la valeur',
    hora: 1,
    why: 'On remplace la variable par sa valeur dans la solution trouvée.',
  },
  compute: { label: 'Calculer', hora: 1, why: 'Un calcul.' },
};

const CARD = 'Équations différentielles';

export interface KOdeVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  /** D7 : ligne juste qui saute des étapes, gardée sans HORA */
  jump?: boolean;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  omove?: OMove;
}

type Of = 'full' | 'homog';

/** ce que la ligne apprend au moteur */
interface OMove {
  text: string;
  kind:
    | 'eq'
    | 'family'
    | 'one'
    | 'deriv'
    | 'eval'
    | 'value'
    | 'ceq'
    | 'char'
    | 'delta'
    | 'roots'
    | 'none';
  of?: Of;
  /** l'écriture de l'équation (réécriture) : isolée ou non */
  shape?: string;
  F?: Term;
  size?: number;
  head?: string;
  order?: number;
  w?: number[];
  r?: number;
  /** « C = −3 » : la constante seule d'un côté, un nombre de l'autre */
  solved?: boolean;
  q?: [number, number, number];
  delta?: number;
  final?: boolean;
}

/** une équation linéaire : a₂ y″ + a₁ y′ + a₀ y = f (coefficients en x) */
interface Ode {
  order: 1 | 2;
  a: [Term, Term, Term];
  f: Term;
}

interface Cond {
  /** l'ordre de la dérivée : y(0) → 0, y′(0) → 1 */
  k: number;
  x0: number;
  /** le bout « y(0) », « y′(0) » */
  head: string;
  v: number;
  text: string;
}

type GoalKind = 'rewrite' | 'general' | 'unique' | 'particular' | 'value';

interface Family {
  of: Of;
  F: Term;
  /** les constantes de la famille, dans l'ordre du modèle */
  cs: string[];
  size: number;
}

export interface KOdeState {
  model: ScratchModel;
  /** la fonction inconnue (« y ») et sa variable (« x ») */
  fn: string;
  x: string;
  Y: [string, string, string];
  /** les constantes permises (« C », « A », « B ») */
  consts: string[];
  ode: Ode;
  /** l'équation de départ est-elle déjà homogène ? */
  homog: boolean;
  /** les coefficients sont-ils des nombres ? */
  constCoef: boolean;
  pts: number[];
  conds: Cond[];
  goal: GoalKind;
  /** la forme de la réponse quand on réécrit l'équation (« y′ » seul à gauche) */
  answerShape: string | null;
  answerText: string;
  /** moteur v63 : la valeur cherchée (« f(0) = … ») */
  target: { x0: number; head: string; arg: string; v: number; size: number } | null;
  /** moteur v63 : les valeurs écrites (« f(0) = … » sans constante), par tête */
  values: Map<string, number>;
  /** les réécritures établies : (de quoi, sous quelle forme) */
  eqs: { of: Of; shape: string }[];
  homogWritten: boolean;
  families: Family[];
  ones: { F: Term; size: number }[];
  derivs: { order: number; F: Term; size: number }[];
  evals: Map<string, number>;
  cEqs: { w: number[]; r: number; solved: boolean }[];
  /** les constantes de la famille à laquelle se rapportent les équations en C */
  cFamily: string[] | null;
  cKnown: boolean;
  charQ: [number, number, number] | null;
  deltas: Set<string>;
  rootsWritten: boolean;
  written: Set<string>;
  givenTexts: Set<string>;
  path: string[];
  traps: { text: string; line: Line; title: string; why: string }[];
  lines: number;
  concluded: boolean;
}

export class OdeModelError extends Error {}

/* ------------------------------ les bouts ------------------------------ */

const P1 = 'ΞΞΞ';
const P2 = 'ΞΞΞΞ';
const SUP_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';
const isSupRun = (a: string): boolean =>
  a.length > 0 && [...a].every((c) => SUP_DIGITS.includes(c));

/**
 * Atomes d'une ligne : « y′ », « y″ » (aussi « y' », « y′′ », « y'' ») sont des bouts ;
 * « y(x) » se lit y, « y′(x) » se lit y′ ; « y(0) », « y′(0) » restent des bouts.
 */
export function kodeLineAtoms(text: string, fn = 'y', x = 'x'): string[] {
  const f = fn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  let t = text
    // une lettre collée devant y′ : « xy′ » = x × y′
    .replace(new RegExp(`([A-Za-z])(?=${f}(?:″|′|'|’))`, 'g'), '$1 ')
    .replace(new RegExp(`(?<![A-Za-zÀ-ÿ])${f}(?:″|′′|''|’’)(?!\\s*\\()`, 'g'), ` ${P2} `)
    .replace(new RegExp(`(?<![A-Za-zÀ-ÿ])${f}(?:′|'|’)(?!\\s*\\()`, 'g'), ` ${P1} `);
  const out: string[] = [];
  for (const a of lex(t)) {
    const last = out[out.length - 1];
    if (isSupRun(a) && last !== undefined && isSupRun(last)) {
      out[out.length - 1] = last + a;
      continue;
    }
    out.push(a);
  }
  const mapped = out.map((a) => {
    if (a === P1) return `${fn}′`;
    if (a === P2) return `${fn}″`;
    if (a === `${fn}(${x})`) return fn;
    if (a === `${fn}′(${x})`) return `${fn}′`;
    if (a === `${fn}′′(${x})`) return `${fn}″`;
    const m = new RegExp(`^${f}′′\\((.+)\\)$`).exec(a);
    if (m) return `${fn}″(${m[1]})`;
    return a;
  });
  return normalize(mapped);
}

/** texte d'une ligne (relu à l'identique par `kodeLineAtoms`) */
export function kodeText(atoms: string[]): string {
  return atomsText(atoms)
    .replace(/ ([⁰¹²³⁴⁵⁶⁷⁸⁹]+)/g, '$1')
    .replace(/\//g, '÷')
    .replace(/\s*÷\s*/g, ' ÷ ');
}

const lineOf = (atoms: string[]): Line => ({
  dis: [{ sides: [{ k: 's', n: kodeText(atoms) }] }],
});
/** Ligne de feuille (texte) pour l'app. */
export function kodeLine(text: string, fn = 'y'): Line {
  return lineOf(kodeLineAtoms(text, fn));
}

/** « x³ » → x ^ 3 pour le parseur (« ² » seul reste un carré) */
function caretAtoms(atoms: string[]): string[] {
  const out: string[] = [];
  for (let i = 0; i < atoms.length; i++) {
    const a = atoms[i]!;
    if (isSupRun(a) && a !== '²') {
      out.push('^', [...a].map((c) => String(SUP_DIGITS.indexOf(c))).join(''));
      continue;
    }
    out.push(a);
  }
  return out;
}

/* ------------------------------ termes et valeurs ------------------------------ */

const E: Term = { k: 'fn', f: 'exp', a: { k: 'num', v: { n: 1n, d: 1n } } } as Term;
/** la constante e s'écrit « e » ; « e^(…) » est déjà lu exp(…) */
function withE(t: Term): Term {
  switch (t.k) {
    case 'sym':
      return t.n === 'e' ? E : t;
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(withE) } as Term;
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: withE(t.a), b: withE(t.b) } as Term;
    case 'fn':
      return { ...t, a: withE(t.a) } as Term;
    default:
      return t;
  }
}

const termOf = (n: Node): Term => withE(fromNode(n));

function val(t: Term, env: Record<string, number>): number {
  try {
    const v = evalFloat(t, env);
    return Number.isFinite(v) ? v : NaN;
  } catch {
    return NaN;
  }
}

function size(t: Term): number {
  switch (t.k) {
    case 'num':
      return t.v.d === 1n ? 1 : 2;
    case 'sym':
      return 1;
    case 'add':
    case 'mul':
      return 1 + t.c.reduce((s, c) => s + size(c), 0);
    case 'div':
    case 'pow':
    case 'binom':
      return 1 + size(t.a) + size(t.b);
    case 'fn':
      return 1 + size(t.a);
  }
}

const keyOf = (t: Term): string => {
  try {
    return termKey(canon(t));
  } catch {
    return termKey(t);
  }
};

const near = (a: number, b: number, scale = 1): boolean =>
  Number.isFinite(a) &&
  Number.isFinite(b) &&
  Math.abs(a - b) <= 1e-7 * Math.max(1, scale, Math.abs(a), Math.abs(b));

function safeDerive(t: Term, x: string): Term | null {
  try {
    return derive(t, x);
  } catch {
    return null;
  }
}

/** valeurs d'essai des constantes (deux constantes au plus) */
const C_SAMPLES: number[][] = [
  [0.7, -1.3],
  [-1.9, 0.45],
  [2.6, 1.7],
];
/** valeurs d'essai de y, y′, y″ (linéarité d'une équation) */
const Y_SAMPLES: number[][] = [
  [0.37, -1.21, 0.83],
  [1.9, 0.44, -0.6],
  [-0.73, 2.1, 1.3],
];
const BASE_PTS = [-2.3, -1.7, -1.15, -0.6, -0.25, 0.2, 0.55, 0.95, 1.35, 1.8, 2.25];
const POS_PTS = [0.3, 0.7, 1.1, 1.6, 2.1, 2.7, 3.3];

/* ------------------------------ équations ------------------------------ */

/** E = gauche − droite, une équation linéaire en y, y′, y″ (null sinon) */
function odeOf(ks: Pick<KOdeState, 'Y' | 'x'>, Et: Term, pts: number[]): Ode | null {
  const [y0, y1, y2] = ks.Y;
  const syms = symbolsOf(Et);
  for (const s of syms)
    if (s !== ks.x && s !== y0 && s !== y1 && s !== y2 && s !== 'π') return null;
  if (!syms.has(y0) && !syms.has(y1) && !syms.has(y2)) return null;
  const a: Term[] = [];
  for (const s of [y0, y1, y2]) {
    const c = safeDerive(Et, s);
    if (!c) return null;
    const cs = symbolsOf(c);
    if (cs.has(y0) || cs.has(y1) || cs.has(y2)) return null;
    a.push(c);
  }
  let rest: Term;
  try {
    rest = canon(substitute(Et, { [y0]: num(0), [y1]: num(0), [y2]: num(0) }));
  } catch {
    return null;
  }
  // E ≡ a₀y + a₁y′ + a₂y″ + rest (linéaire)
  for (const p of pts.slice(0, 5))
    for (const Ys of Y_SAMPLES) {
      const env = { [ks.x]: p, [y0]: Ys[0]!, [y1]: Ys[1]!, [y2]: Ys[2]! };
      const lhs = val(Et, env);
      const rhs = a.reduce((s, c, k) => s + val(c, { [ks.x]: p }) * Ys[k]!, val(rest, env));
      if (!near(lhs, rhs)) return null;
    }
  const nz = (t: Term) => pts.some((p) => Math.abs(val(t, { [ks.x]: p })) > 1e-12);
  const order = nz(a[2]!) ? 2 : nz(a[1]!) ? 1 : 0;
  if (order === 0) return null;
  return { order: order as 1 | 2, a: [a[0]!, a[1]!, a[2]!], f: canon(mul(num(-1), rest)) };
}

/** les coefficients de l'équation en un point : a₀, a₁, a₂, f */
function coefsAt(o: Ode, x: string, p: number): number[] {
  return [...o.a.map((t) => val(t, { [x]: p })), val(o.f, { [x]: p })];
}

/** deux équations équivalentes (proportionnelles en chaque point, sans annuler) */
function sameOde(o1: Ode, o2: Ode, x: string, pts: number[]): boolean {
  if (o1.order !== o2.order) return false;
  return pts.every((p) => {
    const u = coefsAt(o1, x, p);
    const v = coefsAt(o2, x, p);
    let j = 0;
    v.forEach((c, i) => {
      if (Math.abs(c) > Math.abs(v[j]!)) j = i;
    });
    if (!(Math.abs(v[j]!) > 1e-12) || !Number.isFinite(u[j]!)) return false;
    const lam = u[j]! / v[j]!;
    if (!(Math.abs(lam) > 1e-12)) return false;
    return u.every((c, i) => near(c, lam * v[i]!, Math.abs(lam * v[i]!)));
  });
}

const homogOf = (o: Ode): Ode => ({ ...o, f: num(0) });

/* ------------------------------ solutions ------------------------------ */

/** reste de l'équation pour une fonction F (constantes fixées), en chaque point */
function residualOk(ks: KOdeState, o: Ode, F: Term, env: Record<string, number>): boolean {
  const d1 = safeDerive(F, ks.x);
  const d2 = d1 ? safeDerive(d1, ks.x) : null;
  if (!d1 || (o.order === 2 && !d2)) return false;
  const ds = [F, d1, d2 ?? num(0)];
  return ks.pts.every((p) => {
    const e = { ...env, [ks.x]: p };
    const terms = [0, 1, 2].map((k) =>
      k > o.order ? 0 : val(o.a[k]!, { [ks.x]: p }) * val(ds[k]!, e),
    );
    const f = val(o.f, { [ks.x]: p });
    const lhs = terms.reduce((s, t) => s + t, 0);
    const scale = Math.abs(f) + terms.reduce((s, t) => s + Math.abs(t), 0);
    return near(lhs, f, scale);
  });
}

function constsIn(ks: KOdeState, F: Term): string[] {
  const s = symbolsOf(F);
  return ks.consts.filter((c) => s.has(c));
}

/** les lettres de F qui ne sont ni x, ni une constante, ni π */
function strangers(ks: KOdeState, t: Term): string[] {
  return [...symbolsOf(t)].filter((s) => s !== ks.x && s !== 'π' && !ks.consts.includes(s));
}

const envOf = (cs: string[], vals: number[]): Record<string, number> =>
  Object.fromEntries(cs.map((c, i) => [c, vals[i] ?? 0]));

/** F affine en ses constantes : F(c) = F(0) + Σ cᵢ Bᵢ */
function affineIn(ks: KOdeState, F: Term, cs: string[]): boolean {
  const zero = envOf(
    cs,
    cs.map(() => 0),
  );
  return C_SAMPLES.every((cv) =>
    ks.pts.every((p) => {
      const base = val(F, { ...zero, [ks.x]: p });
      let rhs = base;
      cs.forEach((_, i) => {
        const e = envOf(
          cs,
          cs.map((__, j) => (j === i ? 1 : 0)),
        );
        rhs += (cv[i] ?? 0) * (val(F, { ...e, [ks.x]: p }) - base);
      });
      return near(val(F, { ...envOf(cs, cv), [ks.x]: p }), rhs);
    }),
  );
}

/** rang numérique d'une matrice (lignes) */
function rank(rows: number[][]): number {
  const m = rows.map((r) => {
    const s = Math.max(...r.map((v) => Math.abs(v)));
    return s > 0 ? r.map((v) => v / s) : r.slice();
  });
  let rk = 0;
  const cols = m[0]?.length ?? 0;
  for (let c = 0; c < cols && rk < m.length; c++) {
    let piv = rk;
    for (let i = rk + 1; i < m.length; i++)
      if (Math.abs(m[i]![c]!) > Math.abs(m[piv]![c]!)) piv = i;
    if (Math.abs(m[piv]![c]!) < 1e-7) continue;
    [m[rk], m[piv]] = [m[piv]!, m[rk]!];
    for (let i = 0; i < m.length; i++) {
      if (i === rk) continue;
      const f = m[i]![c]! / m[rk]![c]!;
      for (let j = c; j < cols; j++) m[i]![j]! -= f * m[rk]![j]!;
    }
    rk++;
  }
  return rk;
}

/** les fonctions de base Bᵢ = F(eᵢ) − F(0), en chaque point */
function basisRows(ks: KOdeState, F: Term, cs: string[]): number[][] {
  const zero = envOf(
    cs,
    cs.map(() => 0),
  );
  return cs.map((_, i) => {
    const e = envOf(
      cs,
      cs.map((__, j) => (j === i ? 1 : 0)),
    );
    return ks.pts.map((p) => val(F, { ...e, [ks.x]: p }) - val(F, { ...zero, [ks.x]: p }));
  });
}

type SolKind =
  | { k: 'general'; of: Of; cs: string[] }
  | { k: 'partial'; of: Of; cs: string[] }
  | { k: 'one'; of: Of }
  | { k: 'none'; why: 'particular' | 'homog' | 'plain' | 'notAffine' };

function classify(ks: KOdeState, F: Term): SolKind {
  const cs = constsIn(ks, F);
  const tries: [Of, Ode][] = ks.homog
    ? [['full', ks.ode]]
    : [
        ['full', ks.ode],
        ['homog', homogOf(ks.ode)],
      ];
  if (!cs.length) {
    for (const [of, o] of tries) if (residualOk(ks, o, F, {})) return { k: 'one', of };
    return { k: 'none', why: 'plain' };
  }
  if (!affineIn(ks, F, cs)) return { k: 'none', why: 'notAffine' };
  const order = ks.ode.order;
  for (const [of, o] of tries) {
    if (C_SAMPLES.every((cv) => residualOk(ks, o, F, envOf(cs, cv)))) {
      const rk = rank(basisRows(ks, F, cs));
      return rk >= order ? { k: 'general', of, cs } : { k: 'partial', of, cs };
    }
  }
  // la partie avec les constantes est juste (solutions de l'équation homogène), pas la
  // solution particulière
  const zero = envOf(
    cs,
    cs.map(() => 0),
  );
  const P = substitute(F, Object.fromEntries(Object.entries(zero).map(([k, v]) => [k, num(v)])));
  const Bterm = canon(add(F, mul(num(-1), P)));
  if (!ks.homog && C_SAMPLES.every((cv) => residualOk(ks, homogOf(ks.ode), Bterm, envOf(cs, cv))))
    return { k: 'none', why: 'particular' };
  return { k: 'none', why: 'homog' };
}

/** G est-elle une fonction de la famille F ? (les valeurs des constantes, sinon null) */
function memberOf(ks: KOdeState, fam: Family, G: Term): number[] | null {
  const cs = fam.cs;
  const zero = envOf(
    cs,
    cs.map(() => 0),
  );
  const P = ks.pts.map((p) => val(fam.F, { ...zero, [ks.x]: p }));
  const B = basisRows(ks, fam.F, cs);
  const g = ks.pts.map((p) => val(G, { [ks.x]: p }) - P[ks.pts.indexOf(p)]!);
  // moindres carrés (n ≤ 2)
  const n = cs.length;
  const M = Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (__, j) => B[i]!.reduce((s, v, k) => s + v * B[j]![k]!, 0)),
  );
  const rhs = Array.from({ length: n }, (_, i) => B[i]!.reduce((s, v, k) => s + v * g[k]!, 0));
  const c = solveSmall(M, rhs);
  if (!c) return null;
  const ok = ks.pts.every((_, k) =>
    near(
      B.reduce((s, row, i) => s + c[i]! * row[k]!, 0),
      g[k]!,
    ),
  );
  return ok ? c : null;
}

function solveSmall(M: number[][], b: number[]): number[] | null {
  if (M.length === 1) {
    const a = M[0]![0]!;
    return Math.abs(a) > 1e-12 ? [b[0]! / a] : null;
  }
  if (M.length === 2) {
    const [[a, bb], [c, d]] = M as [[number, number], [number, number]];
    const det = a * d - bb * c;
    if (Math.abs(det) < 1e-12 * Math.max(1, Math.abs(a * d), Math.abs(bb * c))) return null;
    return [(b[0]! * d - bb * b[1]!) / det, (a * b[1]! - c * b[0]!) / det];
  }
  return null;
}

/* ------------------------------ équations en C ------------------------------ */

/** une expression en les constantes, affine : (w, r) avec expr = w·c + r */
function affineVec(t: Term, cs: string[]): { w: number[]; r: number } | null {
  const r = val(
    t,
    envOf(
      cs,
      cs.map(() => 0),
    ),
  );
  if (!Number.isFinite(r)) return null;
  const w = cs.map(
    (_, i) =>
      val(
        t,
        envOf(
          cs,
          cs.map((__, j) => (j === i ? 1 : 0)),
        ),
      ) - r,
  );
  if (w.some((v) => !Number.isFinite(v))) return null;
  const ok = C_SAMPLES.every((cv) =>
    near(
      val(t, envOf(cs, cv)),
      w.reduce((s, v, i) => s + v * cv[i]!, r),
    ),
  );
  return ok ? { w, r } : null;
}

/** la famille de référence des équations en C : la plus récente (de l'équation complète) */
function refFamily(ks: KOdeState): Family | null {
  for (let i = ks.families.length - 1; i >= 0; i--)
    if (ks.families[i]!.of === 'full') return ks.families[i]!;
  return null;
}

/** dériver k fois */
function deriveK(t: Term, x: string, k: number): Term | null {
  let u: Term | null = t;
  for (let i = 0; i < k && u; i++) u = safeDerive(u, x);
  return u;
}

/** la condition en équation en C : w·c = r */
function condVec(ks: KOdeState, fam: Family, c: Cond): { w: number[]; r: number } | null {
  const d = deriveK(fam.F, ks.x, c.k);
  if (!d) return null;
  const at = substitute(d, { [ks.x]: num(c.x0) });
  const a = affineVec(at, fam.cs);
  return a ? { w: a.w, r: c.v - a.r } : null;
}

/** les valeurs des constantes fixées par les conditions (null : pas déterminées) */
function cStar(ks: KOdeState, fam: Family): number[] | null {
  const rows = ks.conds.map((c) => condVec(ks, fam, c));
  if (rows.some((r) => !r) || rows.length < fam.cs.length) return null;
  const R = rows as { w: number[]; r: number }[];
  const n = fam.cs.length;
  const M = Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (__, j) => R.reduce((s, row) => s + row.w[i]! * row.w[j]!, 0)),
  );
  const b = Array.from({ length: n }, (_, i) => R.reduce((s, row) => s + row.w[i]! * row.r, 0));
  return solveSmall(M, b);
}

const augmented = (e: { w: number[]; r: number }): number[] => [...e.w, e.r];

function proportional(u: number[], v: number[]): boolean {
  let j = 0;
  v.forEach((c, i) => {
    if (Math.abs(c) > Math.abs(v[j]!)) j = i;
  });
  if (!(Math.abs(v[j]!) > 1e-12)) return false;
  const lam = u[j]! / v[j]!;
  if (!(Math.abs(lam) > 1e-12)) return false;
  return u.every((c, i) => near(c, lam * v[i]!, Math.abs(lam * v[i]!)));
}

/* ------------------------------ équation caractéristique ------------------------------ */

const R_PTS = [-1.3, 0.4, 2.2, 3.1];

/** un polynôme de degré ≤ 2 en r : ses coefficients (q₂, q₁, q₀) */
function quadOf(t: Term, r: string): [number, number, number] | null {
  const v = R_PTS.map((p) => val(t, { [r]: p }));
  if (v.some((x) => !Number.isFinite(x))) return null;
  const [x0, x1, x2] = R_PTS as [number, number, number];
  const [y0, y1, y2] = v as [number, number, number];
  // interpolation de Lagrange sur trois points
  const d0 = (x0 - x1) * (x0 - x2);
  const d1 = (x1 - x0) * (x1 - x2);
  const d2 = (x2 - x0) * (x2 - x1);
  const q2 = y0 / d0 + y1 / d1 + y2 / d2;
  const q1 = (-y0 * (x1 + x2)) / d0 - (y1 * (x0 + x2)) / d1 - (y2 * (x0 + x1)) / d2;
  const q0 = (y0 * x1 * x2) / d0 + (y1 * x0 * x2) / d1 + (y2 * x0 * x1) / d2;
  const x3 = R_PTS[3]!;
  if (!near(q2 * x3 * x3 + q1 * x3 + q0, v[3]!)) return null;
  const clean = (c: number) => (Math.abs(c) < 1e-10 ? 0 : c);
  return [clean(q2), clean(q1), clean(q0)];
}

interface Cx {
  re: number;
  im: number;
}

function roots(q: [number, number, number]): Cx[] {
  const [a, b, c] = q;
  const d = b * b - 4 * a * c;
  if (Math.abs(d) < 1e-12 * Math.max(1, b * b)) return [{ re: -b / (2 * a), im: 0 }];
  if (d > 0) {
    const s = Math.sqrt(d);
    return [
      { re: (-b - s) / (2 * a), im: 0 },
      { re: (-b + s) / (2 * a), im: 0 },
    ];
  }
  const s = Math.sqrt(-d);
  return [
    { re: -b / (2 * a), im: -s / (2 * a) },
    { re: -b / (2 * a), im: s / (2 * a) },
  ];
}

/** la valeur complexe d'une écriture en i (affine en i) */
function cxOf(t: Term): Cx | null {
  const s = symbolsOf(t);
  if ([...s].some((n) => n !== 'i' && n !== 'π')) return null;
  const v0 = val(t, { i: 0 });
  const v1 = val(t, { i: 1 });
  const v2 = val(t, { i: 2 });
  if (![v0, v1, v2].every(Number.isFinite)) return null;
  const im = v1 - v0;
  if (!near(v2, v0 + 2 * im)) return null;
  return { re: v0, im };
}

const cxNear = (a: Cx, b: Cx): boolean => near(a.re, b.re) && near(a.im, b.im);

/* ------------------------------ verdicts ------------------------------ */

type Base = { atoms: string[]; line: Line };

function stepVerdict(
  base: Base,
  kind: StepKind,
  omove: OMove,
  final: boolean,
  label?: string,
  why?: string,
): KOdeVerdict {
  const s = STEP[kind];
  return {
    kind: 'step',
    ...base,
    hora: s.hora,
    final,
    rule: {
      id: ODE_STEP_IDS[kind],
      card: CARD,
      label: label ?? s.label,
      hora: s.hora,
      why: (why ?? s.why) + (final ? ' C’est la réponse.' : ''),
    },
    omove: { ...omove, ...(final ? { final } : {}) },
  };
}

function jumpVerdict(base: Base, omove: OMove, final: boolean, message?: string): KOdeVerdict {
  return {
    kind: 'useless',
    ...base,
    jump: true,
    final,
    title: final ? 'Juste !' : 'Juste, mais tu sautes des étapes',
    message:
      message ??
      (final
        ? 'C’est la réponse. Détaille ton raisonnement, une étape par ligne, pour gagner des HORA. Tu peux garder cette ligne sans HORA.'
        : 'Ta ligne est juste. Détaille-la, une étape à la fois, pour gagner des HORA. Tu peux la garder sans HORA.'),
    omove: { ...omove, ...(final ? { final } : {}) },
  };
}

function useless(base: Base, title: string, message: string, omove?: OMove): KOdeVerdict {
  return { kind: 'useless', ...base, title, message, ...(omove ? { omove } : {}) };
}

function trapOr(ks: KOdeState, base: Base, why: { title: string; message: string }): KOdeVerdict {
  const text = kodeText(base.atoms);
  const t = ks.traps.find((t) => t.text === text);
  if (t)
    return {
      kind: 'trap',
      ...base,
      title: t.title,
      message: t.why,
      trap: { line: t.line, title: t.title, why: t.why },
    };
  return { kind: 'false', ...base, ...why };
}

/* ------------------------------ état ------------------------------ */

function domainFilter(model: ScratchModel, x: string): (p: number) => boolean {
  const d = model.kernel?.domain;
  if (!d) return () => true;
  let rels: { l: Term; r: Term; cmp: string }[] = [];
  try {
    const line = parseLine(normalize(lex(d)), { chains: true });
    rels = line.dis.flatMap((q) => {
      const cmps = q.chain ?? (q.cmp ? [q.cmp] : []);
      return cmps.map((cmp, i) => ({
        l: termOf(q.sides[i]!),
        r: termOf(q.sides[i + 1]!),
        cmp,
      }));
    });
  } catch {
    throw new OdeModelError(`Intervalle illisible : « ${d} ».`);
  }
  return (p) =>
    rels.every(({ l, r, cmp }) => {
      const a = val(l, { [x]: p });
      const b = val(r, { [x]: p });
      if (cmp === '>') return a > b;
      if (cmp === '<') return a < b;
      if (cmp === '≥') return a >= b;
      if (cmp === '≤') return a <= b;
      return true;
    });
}

interface ReadEq {
  k: 'eq';
  /** les membres (le premier : la tête) */
  sides: Node[];
}
type Read =
  | ReadEq
  | { k: 'or'; clauses: Node[][] }
  | { k: 'incomplete' }
  | { k: 'syntax'; title: string; message: string };

function readAtoms(A: string[]): Read {
  let line: Line;
  try {
    line = parseLine(normalize(caretAtoms(A)), { chains: true });
  } catch {
    return { k: 'incomplete' };
  }
  if (line.dis.length > 1) {
    if (line.dis.some((d) => d.cmp || d.chain || d.rels || d.sides.length !== 2))
      return { k: 'syntax', title: 'Des égalités', message: 'Écris « r = … ou r = … ».' };
    return { k: 'or', clauses: line.dis.map((d) => d.sides) };
  }
  const eq = line.dis[0]!;
  if (eq.cmp || eq.chain || eq.rels || eq.mod !== undefined)
    return {
      k: 'syntax',
      title: 'Une égalité',
      message: 'Écris une égalité : « y = … », « y′ = … », « y(0) = … ».',
    };
  if (eq.sides.length < 2) return { k: 'incomplete' };
  return { k: 'eq', sides: eq.sides };
}

function condOf(text: string, fn: string, x: string): Cond {
  const A = kodeLineAtoms(text, fn, x);
  const r = readAtoms(A);
  const bad = () =>
    new OdeModelError(`Une condition s’écrit « ${fn}(0) = 1 » ou « ${fn}′(0) = 0 » : « ${text} ».`);
  if (r.k !== 'eq' || r.sides.length !== 2) throw bad();
  const h = r.sides[0]!;
  const m = h.k === 's' ? headRe(fn).exec(h.n!) : null;
  if (!m) throw bad();
  const k = m[1] === '″' ? 2 : m[1]!.length;
  const x0 = val(exprOfText(m[2]!), {});
  const v = val(termOf(r.sides[1]!), {});
  if (!Number.isFinite(x0) || !Number.isFinite(v)) throw bad();
  return { k, x0, head: h.n!, v, text: kodeText(A) };
}

const headRe = (fn: string): RegExp =>
  new RegExp(`^${fn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(′*|″)\\((.+)\\)$`);

function exprOfText(text: string): Term {
  const line = parseLine(normalize(lex(text)));
  return termOf(line.dis[0]!.sides[0]!);
}

/** moteur v63 : la variable de la feuille (« x » par défaut, « t ») */
export function odeVariable(model: ScratchModel): string {
  return model.kernel?.ode?.variable ?? 'x';
}

/** les lettres de la feuille : la fonction, sa variable, les constantes */
export function odeQuestionOf(model: ScratchModel): {
  fn: string;
  x: string;
  consts: string[];
} {
  const spec = model.kernel!;
  const fn = spec.goal.target;
  if (!/^[a-zA-Z]$/.test(fn))
    throw new OdeModelError('La fonction inconnue est une lettre (« y »).');
  // moteur v63 : la variable peut être t (« y(t) »)
  const x = spec.ode?.variable ?? 'x';
  if (!/^[a-z]$/.test(x) || x === fn || ['e', 'i', 'r'].includes(x))
    throw new OdeModelError('La variable est une lettre (« x », « t ») autre que la fonction.');
  return { fn, x, consts: spec.ode?.constants ?? ['C', 'A', 'B', 'C₁', 'C₂', 'K'] };
}

export function createKOdeState(model: ScratchModel): KOdeState {
  const spec = model.kernel!;
  const q = odeQuestionOf(model);
  const { fn, x } = q;
  const Y: [string, string, string] = [fn, `${fn}′`, `${fn}″`];
  const inDomain = domainFilter(model, x);
  const ks: KOdeState = {
    model,
    fn,
    x,
    Y,
    consts: q.consts,
    ode: { order: 1, a: [num(0), num(0), num(0)], f: num(0) },
    homog: false,
    constCoef: false,
    pts: [],
    conds: [],
    goal: 'general',
    answerShape: null,
    answerText: '',
    target: null,
    values: new Map(),
    eqs: [],
    homogWritten: false,
    families: [],
    ones: [],
    derivs: [],
    evals: new Map(),
    cEqs: [],
    cFamily: null,
    cKnown: false,
    charQ: null,
    deltas: new Set(),
    rootsWritten: false,
    written: new Set(),
    givenTexts: new Set(),
    path: spec.path ?? [],
    traps: [],
    lines: 0,
    concluded: false,
  };
  const startA = kodeLineAtoms(spec.start, fn, x);
  const sr = readAtoms(startA);
  if (sr.k !== 'eq' || sr.sides.length !== 2)
    throw new OdeModelError(`Le départ est une équation : « ${spec.start} ».`);
  const Et = canon(add(termOf(sr.sides[0]!), mul(num(-1), termOf(sr.sides[1]!))));
  const cand = [...BASE_PTS, ...POS_PTS].filter(inDomain);
  const o0 = odeOf(ks, Et, cand);
  if (!o0)
    throw new OdeModelError(
      `Le départ n’est pas une équation différentielle linéaire en ${fn}, ${fn}′, ${fn}″ : « ${spec.start} ».`,
    );
  ks.pts = [...new Set(cand)]
    .filter((p) => {
      const c = coefsAt(o0, x, p);
      return c.every(Number.isFinite) && Math.abs(c[o0.order]!) > 1e-9;
    })
    .sort((a, b) => a - b)
    .slice(0, 11);
  if (ks.pts.length < 6) throw new OdeModelError('Pas assez de points pour vérifier l’équation.');
  ks.ode = o0;
  ks.homog = ks.pts.every((p) => Math.abs(val(o0.f, { [x]: p })) < 1e-12);
  ks.constCoef = [...o0.a, o0.f].every((t) => !symbolsOf(t).has(x));
  // l'écriture de départ (« y′ = … ») ne se recopie pas ; une autre forme se transforme
  if (shapeOf(sr.sides) !== 'autre') ks.eqs.push({ of: 'full', shape: shapeOf(sr.sides) });
  ks.conds = (spec.ode?.initial ?? []).map((t) => condOf(t, fn, x));
  if (ks.conds.some((c) => c.k >= o0.order))
    throw new OdeModelError(`Une condition porte sur ${fn} ou ses dérivées d’ordre < ${o0.order}.`);
  // la réponse
  const ansA = kodeLineAtoms(spec.answer, fn, x);
  ks.answerText = kodeText(ansA);
  const ar = readAtoms(ansA);
  if (ar.k !== 'eq' || ar.sides.length !== 2)
    throw new OdeModelError(`La réponse est une égalité : « ${spec.answer} ».`);
  const h = ar.sides[0]!;
  const rhs = termOf(ar.sides[1]!);
  const hasY = [...symbolsOf(rhs)].some((s) => ks.Y.includes(s));
  // moteur v63 : la valeur de la solution en un point (« f(0) = 5 × e^(−2) »)
  const vm = h.k === 's' ? headRe(fn).exec(h.n!) : null;
  if (vm && vm[1] === '' && [...symbolsOf(rhs)].every((s) => s === 'π')) {
    const x0 = val(exprOfText(vm[2]!), {});
    const v = val(rhs, {});
    if (!Number.isFinite(x0) || !Number.isFinite(v))
      throw new OdeModelError(`La réponse est une valeur : « ${fn}(0) = … ».`);
    if (ks.conds.length < o0.order)
      throw new OdeModelError(`Il faut ${o0.order} conditions pour trouver la valeur.`);
    if (ks.conds.some((c) => c.k === 0 && near(c.x0, x0)))
      throw new OdeModelError('La valeur cherchée est déjà une condition de l’énoncé.');
    const ref = odeValueAt(ks, x0);
    if (ref === null)
      throw new OdeModelError(
        'Les conditions ne fixent pas la solution (à l’ordre 2 : y et y′ au même point).',
      );
    if (!near(v, ref, Math.abs(ref)))
      throw new OdeModelError(`La réponse n’est pas la valeur de la solution (${ref}).`);
    ks.goal = 'value';
    ks.target = { x0, head: h.n!, arg: vm[2]!, v: ref, size: size(rhs) };
  } else if (h.k === 's' && h.n === fn && !hasY) {
    const cs = constsIn(ks, rhs);
    ks.goal = cs.length ? 'general' : ks.conds.length ? 'unique' : 'particular';
    if (ks.goal === 'unique' && ks.conds.length < o0.order)
      throw new OdeModelError(`Il faut ${o0.order} conditions pour une seule solution.`);
    const c = classify(ks, rhs);
    if (ks.goal === 'general' && !(c.k === 'general' && c.of === 'full'))
      throw new OdeModelError('La réponse n’est pas l’ensemble des solutions.');
    if (ks.goal !== 'general' && !(c.k === 'one' && c.of === 'full'))
      throw new OdeModelError('La réponse n’est pas une solution de l’équation.');
    if (ks.goal === 'unique' && !condsHold(ks, rhs))
      throw new OdeModelError('La réponse ne vérifie pas les conditions.');
  } else {
    const o = lineOde(ks, ar.sides);
    if (!o || !sameOde(o, ks.ode, x, ks.pts))
      throw new OdeModelError('La réponse n’est pas l’équation réécrite.');
    ks.goal = 'rewrite';
    ks.answerShape = shapeOf(ar.sides);
  }
  ks.givenTexts = new Set(odeGivenTexts(model).map((t) => kodeText(kodeLineAtoms(t, fn, x))));
  ks.traps = (model.traps ?? []).map((t) => ({
    text: kodeText(kodeLineAtoms(t.line, fn, x)),
    line: kodeLine(t.line, fn),
    title: t.title ?? 'Attention',
    why: t.why,
  }));
  return ks;
}

/** les lignes de l'énoncé : l'équation, les conditions, puis l'intervalle (« x > 0 ») */
export function odeGivenTexts(model: ScratchModel): string[] {
  const k = model.kernel!;
  return [k.start, ...(k.ode?.initial ?? []), ...(k.domain ? [k.domain] : [])];
}

/** la forme d'une équation : ce qui est seul à gauche (« y′ ») ou « autre » */
function shapeOf(sides: Node[]): string {
  const h = sides[0]!;
  return h.k === 's' ? `h:${h.n}` : 'autre';
}

function lineOde(ks: KOdeState, sides: Node[]): Ode | null {
  if (sides.length !== 2) return null;
  let Et: Term;
  try {
    Et = canon(add(termOf(sides[0]!), mul(num(-1), termOf(sides[1]!))));
  } catch {
    return null;
  }
  return odeOf(ks, Et, ks.pts.length ? ks.pts : BASE_PTS);
}

/**
 * moteur v63 : la valeur en xt de la solution qui vérifie les conditions, par Runge-Kutta 4
 * depuis le point des conditions (à l'ordre 2 : y et y′ au même point). null : pas fixée.
 */
function odeValueAt(ks: KOdeState, xt: number): number | null {
  const o = ks.ode;
  const c0 = ks.conds.find((c) => c.k === 0);
  if (!c0) return null;
  const c1 = o.order === 2 ? ks.conds.find((c) => c.k === 1 && near(c.x0, c0.x0)) : null;
  if (o.order === 2 && !c1) return null;
  if (near(xt, c0.x0)) return c0.v;
  const fixed = ks.constCoef ? coefsAt(o, ks.x, c0.x0) : null;
  const N = ks.constCoef ? 2000 : 600;
  const h = (xt - c0.x0) / N;
  const rhs = (p: number, s: number[]): number[] => {
    const [a0, a1, a2, f] = fixed ?? coefsAt(o, ks.x, p);
    if (o.order === 1) return [(f! - a0! * s[0]!) / a1!];
    return [s[1]!, (f! - a1! * s[1]! - a0! * s[0]!) / a2!];
  };
  let s = o.order === 1 ? [c0.v] : [c0.v, c1!.v];
  let p = c0.x0;
  for (let i = 0; i < N; i++) {
    const k1 = rhs(p, s);
    const k2 = rhs(
      p + h / 2,
      s.map((v, j) => v + (h / 2) * k1[j]!),
    );
    const k3 = rhs(
      p + h / 2,
      s.map((v, j) => v + (h / 2) * k2[j]!),
    );
    const k4 = rhs(
      p + h,
      s.map((v, j) => v + h * k3[j]!),
    );
    s = s.map((v, j) => v + (h / 6) * (k1[j]! + 2 * k2[j]! + 2 * k3[j]! + k4[j]!));
    p += h;
  }
  return s.every(Number.isFinite) ? s[0]! : null;
}

function condsHold(ks: KOdeState, F: Term): boolean {
  return ks.conds.every((c) => {
    const d = deriveK(F, ks.x, c.k);
    return !!d && near(val(d, { [ks.x]: c.x0 }), c.v);
  });
}

/* ------------------------------ jugement ------------------------------ */

function knownPrefix(ks: KOdeState, A: string[]): boolean {
  return ks.path.some((t) => {
    let atoms: string[];
    try {
      atoms = kodeLineAtoms(t, ks.fn, ks.x);
    } catch {
      return false;
    }
    return atoms.length > A.length && A.every((a, i) => atoms[i] === a);
  });
}

export function kodeJudge(ks: KOdeState, chips: Chip[], opts?: { verify?: boolean }): KOdeVerdict {
  const raw = chips.flatMap((c) => c.atoms);
  const A = normalize(raw);
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const prefix = knownPrefix(ks, A);
  const incomplete = (): KOdeVerdict =>
    opts?.verify
      ? { kind: 'syntax', atoms: A, title: 'Ligne inachevée', message: 'Termine ta ligne.' }
      : { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  const r = readAtoms(A);
  if (r.k === 'incomplete') return incomplete();
  if (r.k === 'syntax') {
    if (prefix && !opts?.verify) return { kind: 'building', atoms: A, complete: false };
    return { kind: 'syntax', atoms: A, line: lineOf(A), title: r.title, message: r.message };
  }
  const base: Base = { atoms: A, line: lineOf(A) };
  let v: KOdeVerdict;
  try {
    v = judgeRead(ks, base, r);
  } catch {
    v = {
      kind: 'syntax',
      ...base,
      title: 'Ça ne se lit pas',
      message: 'Cette ligne ne se lit pas : vérifie l’écriture.',
    };
  }
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return { ...v, complete: true };
}

function judgeRead(
  ks: KOdeState,
  base: Base,
  r: ReadEq | { k: 'or'; clauses: Node[][] },
): KOdeVerdict {
  const text = kodeText(base.atoms);
  if (ks.givenTexts.has(text))
    return useless(
      base,
      'C’est l’énoncé',
      'Cette ligne est dans l’énoncé : écris l’étape suivante.',
    );
  if (ks.written.has(text))
    return useless(base, 'Déjà écrit', 'Tu as déjà écrit cette ligne : écris l’étape suivante.');
  if (r.k === 'or') return judgeRoots(ks, base, r.clauses, text);
  const sides = r.sides;
  const h = sides[0]!;
  const rest = sides.slice(1).map(termOf);
  const restSyms = new Set(rest.flatMap((t) => [...symbolsOf(t)]));
  const restHasY = ks.Y.some((s) => restSyms.has(s));
  const hn = h.k === 's' ? h.n! : null;
  // y = … (une fonction, une famille)
  if (hn === ks.fn && !restHasY) return judgeSolution(ks, base, rest, text);
  // y′ = … avec des constantes : la dérivée des solutions
  if (
    (hn === ks.Y[1] || hn === ks.Y[2]) &&
    !restHasY &&
    rest.some((t) => constsIn(ks, t).length > 0)
  )
    return judgeDeriv(ks, base, hn === ks.Y[1] ? 1 : 2, rest, text);
  // y(0) = …
  if (hn && headRe(ks.fn).test(hn)) return judgeEval(ks, base, hn, rest, text);
  // Δ = …
  if (hn === 'Δ') return judgeDelta(ks, base, rest, text);
  const all = new Set([...(h.k === 's' ? [hn!] : [...symbolsOf(termOf(h))]), ...restSyms]);
  if (ks.Y.some((s) => all.has(s))) {
    if (sides.length !== 2)
      return {
        kind: 'syntax',
        ...base,
        title: 'Une équation',
        message: 'Écris l’équation sur une ligne : « y′ = … ».',
      };
    return judgeEq(ks, base, sides, text);
  }
  const letters = [...all].filter((s) => s !== 'π');
  if (letters.length && letters.every((s) => s === 'r')) {
    if (hn === 'r' && sides.length === 2 && !symbolsOf(rest[0]!).has('r'))
      return judgeRoots(ks, base, [sides], text);
    return judgeChar(ks, base, sides, text);
  }
  if (letters.length && letters.every((s) => ks.consts.includes(s)))
    return judgeCEq(ks, base, sides, text);
  return {
    kind: 'syntax',
    ...base,
    title: 'Ça ne se lit pas',
    message: `Écris « ${ks.fn} = … », « ${ks.fn}′ = … », « ${ks.fn}(0) = … » ou une équation en la constante.`,
  };
}

/* ── une équation (réécrite, homogène) ── */

function judgeEq(ks: KOdeState, base: Base, sides: Node[], text: string): KOdeVerdict {
  const o = lineOde(ks, sides);
  if (!o)
    return {
      kind: 'false',
      ...base,
      title: 'Pas une équation linéaire',
      message: `L’équation s’écrit avec ${ks.fn}, ${ks.fn}′${ks.ode.order === 2 ? `, ${ks.fn}″` : ''}, des nombres et ${ks.x}.`,
    };
  const shape = shapeOf(sides);
  const isolated = sides[0]!.k === 's' && (sides[0]!.n === ks.Y[1] || sides[0]!.n === ks.Y[2]);
  const label = isolated ? `Isoler ${sides[0]!.n}` : STEP.rewrite.label;
  if (sameOde(o, ks.ode, ks.x, ks.pts)) {
    if (ks.eqs.some((e) => e.of === 'full' && e.shape === shape))
      return useless(
        base,
        'Rien de nouveau',
        'C’est la même équation, écrite autrement : passe à l’étape suivante.',
      );
    const final = ks.goal === 'rewrite' && shape === ks.answerShape;
    return stepVerdict(base, 'rewrite', { text, kind: 'eq', of: 'full', shape }, final, label);
  }
  if (!ks.homog && sameOde(o, homogOf(ks.ode), ks.x, ks.pts)) {
    if (ks.homogWritten) {
      if (ks.eqs.some((e) => e.of === 'homog' && e.shape === shape))
        return useless(base, 'Rien de nouveau', 'Tu as déjà écrit l’équation homogène.');
      return stepVerdict(base, 'rewrite', { text, kind: 'eq', of: 'homog', shape }, false, label);
    }
    return stepVerdict(base, 'homogeneous', { text, kind: 'eq', of: 'homog', shape }, false);
  }
  return trapOr(ks, base, {
    title: 'Pas la même équation',
    message:
      'Ce n’est pas une réécriture de l’équation : un terme qui change de côté change de signe, et on divise tous les termes par le même nombre.',
  });
}

/* ── y = … ── */

function generalLabel(ks: KOdeState, of: Of): { label: string; why: string } {
  const o = of === 'homog' ? homogOf(ks.ode) : ks.ode;
  const fn = ks.fn;
  if (o.order === 1) {
    const noY = ks.pts.every((p) => Math.abs(val(o.a[0]!, { [ks.x]: p })) < 1e-12);
    if (noY)
      return {
        label: 'Primitives',
        why: `Les solutions de ${fn}′ = f(${ks.x}) sont les primitives de f : F(${ks.x}) + C.`,
      };
    const fZero = of === 'homog' || ks.homog;
    if (ks.constCoef)
      return fZero
        ? {
            label: `Solutions de ${fn}′ = a${fn}`,
            why: `Les solutions de ${fn}′ = a${fn} sont les fonctions ${ks.x} ↦ Ce^(a${ks.x}), C réel.`,
          }
        : {
            label: `Solutions de ${fn}′ = a${fn} + b`,
            why: `Les solutions de ${fn}′ = a${fn} + b (a ≠ 0) sont les fonctions ${ks.x} ↦ Ce^(a${ks.x}) − b ÷ a, C réel.`,
          };
    return fZero
      ? {
          label: `Solutions de ${fn}′ + a(${ks.x})${fn} = 0`,
          why: `Les solutions de ${fn}′ + a(${ks.x})${fn} = 0 sont les fonctions ${ks.x} ↦ Ce^(−A(${ks.x})), A une primitive de a.`,
        }
      : { label: STEP.general.label, why: STEP.general.why };
  }
  if (ks.constCoef) {
    const [a0, a1, a2] = o.a.map((t) => val(t, {})) as [number, number, number];
    const d = a1 * a1 - 4 * a2 * a0;
    const prefix = of === 'homog' && !ks.homog ? 'Solutions de l’équation homogène' : 'Solutions';
    if (Math.abs(d) < 1e-12 * Math.max(1, a1 * a1))
      return {
        label: `${prefix} : racine double`,
        why: `Une racine double r : ${fn} = (C₁${ks.x} + C₂)e^(r${ks.x}).`,
      };
    if (d > 0)
      return {
        label: `${prefix} : deux racines réelles`,
        why: `Deux racines réelles r₁ et r₂ : ${fn} = C₁e^(r₁${ks.x}) + C₂e^(r₂${ks.x}).`,
      };
    return {
      label: `${prefix} : racines complexes`,
      why: `Deux racines complexes α ± iβ : ${fn} = e^(α${ks.x})(C₁cos(β${ks.x}) + C₂sin(β${ks.x})).`,
    };
  }
  return { label: STEP.general.label, why: STEP.general.why };
}

function judgeSolution(ks: KOdeState, base: Base, rest: Term[], text: string): KOdeVerdict {
  const F = rest[rest.length - 1]!;
  const odd = strangers(ks, F);
  if (odd.length)
    return {
      kind: 'syntax',
      ...base,
      title: 'Une lettre de trop',
      message: `« ${odd[0]} » n’est ni ${ks.x} ni une constante de la feuille.`,
    };
  // une chaîne : chaque membre est la même fonction
  if (rest.length > 1) {
    const ref = rest[0]!;
    const cs = [...new Set(rest.flatMap((t) => constsIn(ks, t)))];
    const same = rest.every((t) =>
      C_SAMPLES.every((cv) =>
        ks.pts.every((p) =>
          near(val(t, { ...envOf(cs, cv), [ks.x]: p }), val(ref, { ...envOf(cs, cv), [ks.x]: p })),
        ),
      ),
    );
    if (!same)
      return trapOr(ks, base, {
        title: 'Pas la même fonction',
        message: 'Les membres de ta ligne ne sont pas égaux : vérifie ton calcul.',
      });
  }
  const c = classify(ks, F);
  const sz = size(F);
  if (c.k === 'general') {
    const already = ks.families.filter((f) => f.of === c.of);
    if (already.length) {
      if (sz < Math.min(...already.map((f) => f.size)))
        return stepVerdict(
          base,
          'compute',
          { text, kind: 'family', of: c.of, F, size: sz },
          c.of === 'full' && ks.goal === 'general',
        );
      return useless(
        base,
        'Rien de nouveau',
        'Ce sont les mêmes solutions, écrites autrement : passe à l’étape suivante.',
        { text, kind: 'family', of: c.of, F, size: sz },
      );
    }
    const final = c.of === 'full' && ks.goal === 'general';
    if (c.of === 'homog') {
      const g = generalLabel(ks, 'homog');
      return stepVerdict(
        base,
        'homogSolutions',
        { text, kind: 'family', of: 'homog', F, size: sz },
        false,
        ks.ode.order === 2 && ks.constCoef ? g.label : STEP.homogSolutions.label,
        g.why,
      );
    }
    const hasHomog = ks.families.some((f) => f.of === 'homog');
    if (hasHomog && ks.ones.length)
      return stepVerdict(
        base,
        'superpose',
        { text, kind: 'family', of: 'full', F, size: sz },
        final,
      );
    const g = generalLabel(ks, 'full');
    return stepVerdict(
      base,
      'general',
      { text, kind: 'family', of: 'full', F, size: sz },
      final,
      g.label,
      g.why,
    );
  }
  if (c.k === 'partial')
    return trapOr(ks, base, {
      title: 'Il manque des solutions',
      message:
        ks.ode.order === 2
          ? 'Une équation d’ordre 2 a deux solutions indépendantes : il faut deux constantes.'
          : 'Toutes les solutions : il faut une constante C.',
    });
  if (c.k === 'one') {
    if (c.of === 'homog')
      return trapOr(ks, base, {
        title: 'Solution de l’équation homogène',
        message: `Cette fonction vérifie l’équation homogène, pas l’équation complète : il manque le second membre.`,
      });
    const omove: OMove = { text, kind: 'one', F, size: sz };
    if (ks.ones.some((o) => near(fnDistance(ks, o.F, F), 0))) {
      const prev = ks.ones.filter((o) => near(fnDistance(ks, o.F, F), 0));
      const final = isFinalOne(ks, F);
      if (sz < Math.min(...prev.map((o) => o.size)))
        return stepVerdict(base, 'compute', omove, final);
      return useless(base, 'Rien de nouveau', 'C’est la même fonction, écrite autrement.', omove);
    }
    const isConst = ks.pts.every((p) =>
      near(val(F, { [ks.x]: p }), val(F, { [ks.x]: ks.pts[0]! })),
    );
    const fam = refFamily(ks);
    // une solution particulière (constante ou « évidente ») de l'équation complète
    const canParticular = !ks.homog && !ks.ones.length;
    const particularStep = (): KOdeVerdict =>
      stepVerdict(base, isConst ? 'constant' : 'particular', omove, false);
    if (ks.goal === 'unique' || ks.goal === 'value') {
      // moteur v63 : la solution trouvée n'est pas encore la réponse quand on cherche une valeur
      const last = ks.goal === 'unique';
      if (condsHold(ks, F)) {
        if (fam && ks.cKnown && ks.cFamily && memberOf(ks, fam, F))
          return stepVerdict(base, 'member', omove, last, memberLabel(fam));
        return jumpVerdict(
          base,
          omove,
          last,
          fam
            ? `C’est la solution. Montre comment tu trouves ${fam.cs.join(' et ')} : écris ce que donne ${ks.conds.map((c) => c.text).join(' et ')}. Tu peux garder cette ligne sans HORA.`
            : undefined,
        );
      }
      if (canParticular) return particularStep();
      const bad = ks.conds.find((cd) => {
        const d = deriveK(F, ks.x, cd.k);
        return !d || !near(val(d, { [ks.x]: cd.x0 }), cd.v);
      });
      return useless(
        base,
        'Juste, mais à côté',
        `Cette fonction est bien une solution, mais elle ne vérifie pas ${bad?.text ?? 'les conditions'}.`,
      );
    }
    if (ks.goal === 'particular') {
      if (fam && memberOf(ks, fam, F))
        return stepVerdict(base, 'member', omove, true, 'Un cas particulier');
      if (isConst) return stepVerdict(base, 'constant', omove, true);
      return jumpVerdict(base, omove, true);
    }
    if (ks.goal === 'general' && canParticular) return particularStep();
    return useless(
      base,
      'Juste, mais à côté',
      ks.goal === 'general'
        ? `C’est une solution, mais la question demande toutes les solutions (avec une constante).`
        : `C’est une solution, mais la question demande de réécrire l’équation.`,
      omove,
    );
  }
  // pas des solutions
  if (c.why === 'notAffine')
    return trapOr(ks, base, {
      title: 'Pas toutes les solutions',
      message:
        'Les solutions s’écrivent avec la constante en facteur : C × (une fonction) + (une solution).',
    });
  if (c.why === 'particular')
    return trapOr(ks, base, {
      title: 'La solution particulière',
      message:
        'La partie avec la constante est juste, mais la fonction qu’on ajoute n’est pas une solution de l’équation complète.',
    });
  return trapOr(ks, base, {
    title: constsIn(ks, F).length ? 'Ce ne sont pas des solutions' : 'Pas une solution',
    message: `Vérifie : avec ${ks.fn} = …, calcule ${ks.fn}′${ks.ode.order === 2 ? ` et ${ks.fn}″` : ''}, puis remplace dans l’équation : on ne retrouve pas l’égalité.`,
  });
}

function memberLabel(fam: Family): string {
  return fam.cs.length > 1 ? `Remplacer ${fam.cs.join(' et ')}` : `Remplacer ${fam.cs[0]}`;
}

function isFinalOne(ks: KOdeState, F: Term): boolean {
  if (ks.goal === 'unique') return condsHold(ks, F);
  return ks.goal === 'particular';
}

/** moteur v63 : une valeur qui se calcule encore (e⁰, × 1, + 0, deux nombres à réunir) */
function reducible(t: Term): boolean {
  const isNumT = (u: Term) => u.k === 'num';
  const isZero = (u: Term) => u.k === 'num' && u.v.n === 0n;
  const isOne = (u: Term) => u.k === 'num' && u.v.n === u.v.d;
  switch (t.k) {
    case 'num':
    case 'sym':
      return false;
    case 'add':
      return t.c.some(isZero) || t.c.filter(isNumT).length > 1 || t.c.some(reducible);
    case 'mul':
      return (
        t.c.some(isZero) || t.c.some(isOne) || t.c.filter(isNumT).length > 1 || t.c.some(reducible)
      );
    case 'div':
      return (
        isOne(t.b) ||
        isZero(t.a) ||
        (isNumT(t.a) && isNumT(t.b)) ||
        reducible(t.a) ||
        reducible(t.b)
      );
    case 'pow':
      return (
        isZero(t.b) ||
        isOne(t.b) ||
        (isNumT(t.a) && isNumT(t.b)) ||
        reducible(t.a) ||
        reducible(t.b)
      );
    case 'fn':
      return (t.f === 'exp' && isZero(t.a)) || (t.f === 'ln' && isOne(t.a)) || reducible(t.a);
    default:
      return false;
  }
}

/** moteur v63 : « f(0) = 5 × e^(−2) », la valeur de la solution au point demandé */
function judgeValue(
  ks: KOdeState,
  base: Base,
  head: string,
  rest: Term[],
  text: string,
): KOdeVerdict {
  const tgt = ks.target!;
  const odd = [...new Set(rest.flatMap((t) => [...symbolsOf(t)]))].filter((s) => s !== 'π');
  if (odd.length)
    return {
      kind: 'syntax',
      ...base,
      title: odd.includes(ks.x) ? 'Une valeur' : 'Une lettre de trop',
      message: odd.includes(ks.x)
        ? `${head} est un nombre : remplace ${ks.x} par ${tgt.arg}.`
        : `« ${odd[0]} » n’a pas sa place dans une valeur.`,
    };
  if (!rest.every((t) => near(val(t, {}), tgt.v, Math.abs(tgt.v))))
    return trapOr(ks, base, {
      title: 'Pas la bonne valeur',
      message: `Remplace ${ks.x} par ${tgt.arg} dans la solution qui vérifie ${ks.conds.map((c) => c.text).join(' et ')}, puis calcule.`,
    });
  const F = rest[rest.length - 1]!;
  const sz = size(F);
  const final = sz <= tgt.size + 2 && !reducible(F);
  const omove: OMove = { text, kind: 'value', head, size: sz };
  const prev = ks.values.get(head);
  if (prev !== undefined) {
    // plus courte, ou enfin calculée (« 5 × e^(−2) × e⁰ » → « 5 ÷ e² »)
    if (sz < prev || (final && !ks.concluded)) return stepVerdict(base, 'compute', omove, final);
    return useless(base, 'Rien de nouveau', `Tu as déjà calculé ${head}.`);
  }
  const known = (ks.cKnown && !!refFamily(ks)) || ks.ones.some((o) => condsHold(ks, o.F));
  if (!known)
    return jumpVerdict(
      base,
      omove,
      final,
      `${final ? 'C’est la réponse.' : 'Ta ligne est juste.'} Montre comment : trouve d’abord la solution qui vérifie ${ks.conds.map((c) => c.text).join(' et ')}, puis remplace ${ks.x} par ${tgt.arg}. Tu peux la garder sans HORA.`,
    );
  return stepVerdict(
    base,
    'value',
    omove,
    final,
    `Calculer ${head}`,
    `On remplace ${ks.x} par ${tgt.arg} dans la solution trouvée.`,
  );
}

/** distance entre deux fonctions (0 : les mêmes) */
function fnDistance(ks: KOdeState, F: Term, G: Term): number {
  return Math.max(...ks.pts.map((p) => Math.abs(val(F, { [ks.x]: p }) - val(G, { [ks.x]: p }))));
}

/* ── y′ = … (dérivée des solutions) ── */

function judgeDeriv(
  ks: KOdeState,
  base: Base,
  order: number,
  rest: Term[],
  text: string,
): KOdeVerdict {
  const G = rest[rest.length - 1]!;
  if (strangers(ks, G).length)
    return {
      kind: 'syntax',
      ...base,
      title: 'Une lettre de trop',
      message: `« ${strangers(ks, G)[0]} » n’est ni ${ks.x} ni une constante de la feuille.`,
    };
  const fams = ks.families;
  if (!fams.length)
    return {
      kind: 'unjustified',
      ...base,
      title: 'D’abord les solutions',
      message: `Écris d’abord les solutions : « ${ks.fn} = … ».`,
    };
  const sameFn = (a: Term, b: Term, cs: string[]) =>
    C_SAMPLES.every((cv) =>
      ks.pts.every((p) =>
        near(val(a, { ...envOf(cs, cv), [ks.x]: p }), val(b, { ...envOf(cs, cv), [ks.x]: p })),
      ),
    );
  for (let i = fams.length - 1; i >= 0; i--) {
    const fam = fams[i]!;
    const d = deriveK(fam.F, ks.x, order);
    if (d && rest.every((t) => sameFn(t, d, fam.cs))) {
      const sz = size(G);
      const prev = ks.derivs.filter((x) => x.order === order && sameFn(x.F, d, fam.cs));
      const omove: OMove = { text, kind: 'deriv', F: G, size: sz, order };
      if (prev.length) {
        if (sz < Math.min(...prev.map((x) => x.size)))
          return stepVerdict(base, 'compute', omove, false);
        return useless(base, 'Rien de nouveau', 'Tu as déjà écrit cette dérivée.');
      }
      return stepVerdict(
        base,
        'derive',
        omove,
        false,
        order === 2 ? `Dériver deux fois` : 'Dériver',
      );
    }
  }
  return trapOr(ks, base, {
    title: 'Pas la dérivée',
    message: `Dérive chaque terme des solutions ; ${fams[fams.length - 1]!.cs.join(', ')} reste${fams[fams.length - 1]!.cs.length > 1 ? 'nt' : ''} une constante.`,
  });
}

/* ── y(0) = … ── */

function judgeEval(
  ks: KOdeState,
  base: Base,
  head: string,
  rest: Term[],
  text: string,
): KOdeVerdict {
  const m = headRe(ks.fn).exec(head)!;
  const k = m[1] === '″' ? 2 : m[1]!.length;
  let x0: number;
  try {
    x0 = val(exprOfText(m[2]!), {});
  } catch {
    x0 = NaN;
  }
  if (!Number.isFinite(x0))
    return {
      kind: 'syntax',
      ...base,
      title: 'Un nombre',
      message: `Écris la valeur de ${ks.x} : « ${ks.fn}(0) ».`,
    };
  const cond = ks.conds.find((c) => c.k === k && near(c.x0, x0));
  const noConst = rest.every((t) => !constsIn(ks, t).length);
  // moteur v63 : la valeur cherchée (« f(0) = … » sans constante)
  const tgt =
    ks.goal === 'value' && ks.target && k === 0 && near(x0, ks.target.x0) ? ks.target : null;
  if (tgt && noConst) return judgeValue(ks, base, head, rest, text);
  if (noConst && cond && rest.every((t) => near(val(t, {}), cond.v)))
    return useless(base, 'C’est l’énoncé', `${cond.text} : c’est une donnée de l’énoncé.`);
  const fam = refFamily(ks);
  if (!fam)
    return {
      kind: 'unjustified',
      ...base,
      title: 'D’abord les solutions',
      message: `Écris d’abord toutes les solutions : « ${ks.fn} = … », avec la constante.`,
    };
  const d = deriveK(fam.F, ks.x, k);
  const at = d ? substitute(d, { [ks.x]: num(x0) }) : null;
  const want = at ? affineVec(at, fam.cs) : null;
  const ok =
    !!want &&
    rest.every((t) => {
      if (strangers(ks, t).length) return false;
      const a = affineVec(t, fam.cs);
      return !!a && near(a.r, want.r) && a.w.every((w, i) => near(w, want.w[i]!));
    });
  if (!ok)
    return trapOr(ks, base, {
      title: 'Pas la bonne valeur',
      message: `Remplace ${ks.x} par ${m[2]} dans ${k ? `la dérivée des solutions` : 'les solutions'}, puis calcule.`,
    });
  const sz = size(rest[rest.length - 1]!);
  const omove: OMove = { text, kind: 'eval', head, size: sz };
  const prev = ks.evals.get(head);
  if (prev !== undefined) {
    if (sz < prev) return stepVerdict(base, 'compute', omove, false);
    return useless(base, 'Rien de nouveau', `Tu as déjà calculé ${head}.`);
  }
  if (!cond && !tgt)
    return useless(
      base,
      'Juste, mais à côté',
      `${head} n’est pas dans les conditions de l’énoncé : calcule ${ks.conds.map((c) => c.head).join(' et ') || 'autre chose'}.`,
    );
  return stepVerdict(
    base,
    'evaluate',
    omove,
    false,
    `Calculer ${head}`,
    `On remplace ${ks.x} par ${m[2]} dans ${k ? 'la dérivée des solutions' : 'les solutions'}.`,
  );
}

/* ── C + 4 = 1, C = −3 ── */

function judgeCEq(ks: KOdeState, base: Base, sides: Node[], text: string): KOdeVerdict {
  const fam = refFamily(ks);
  if (!fam)
    return {
      kind: 'unjustified',
      ...base,
      title: 'D’abord les solutions',
      message: `Écris d’abord toutes les solutions : « ${ks.fn} = … », avec la constante.`,
    };
  if (sides.length !== 2)
    return {
      kind: 'syntax',
      ...base,
      title: 'Une égalité',
      message: 'Une seule égalité par ligne.',
    };
  const t = canon(add(termOf(sides[0]!), mul(num(-1), termOf(sides[1]!))));
  const odd = [...symbolsOf(t)].filter((s) => s !== 'π' && !fam.cs.includes(s));
  if (odd.length)
    return {
      kind: 'syntax',
      ...base,
      title: 'Une lettre de trop',
      message: `Les constantes des solutions sont ${fam.cs.join(' et ')}.`,
    };
  const a = affineVec(t, fam.cs);
  if (!a)
    return {
      kind: 'false',
      ...base,
      title: 'Une équation du premier degré',
      message: `La condition donne une équation du premier degré en ${fam.cs.join(' et ')}.`,
    };
  const eq = { w: a.w, r: -a.r };
  if (eq.w.every((w) => Math.abs(w) < 1e-12))
    return useless(base, 'Rien de nouveau', `Ta ligne ne dit rien sur ${fam.cs.join(' ni ')}.`);
  const star = cStar(ks, fam);
  if (!star)
    return useless(
      base,
      'Rien ne fixe la constante',
      `L’énoncé ne donne pas de condition pour trouver ${fam.cs.join(' et ')}.`,
    );
  const lhs = eq.w.reduce((s, w, i) => s + w * star[i]!, 0);
  if (!near(lhs, eq.r))
    return trapOr(ks, base, {
      title: 'Pas la bonne équation',
      message: `Écris ce que donne${ks.conds.length > 1 ? 'nt' : ''} ${ks.conds.map((c) => c.text).join(' et ')} avec les solutions.`,
    });
  const sameFam = ks.cFamily && ks.cFamily.join() === fam.cs.join();
  const est = sameFam ? ks.cEqs : [];
  const isSym = (n: Node, ok: (s: string) => boolean) => n.k === 's' && ok(n.n!);
  const solved =
    (isSym(sides[0]!, (s) => fam.cs.includes(s)) && !constsIn(ks, termOf(sides[1]!)).length) ||
    (isSym(sides[1]!, (s) => fam.cs.includes(s)) && !constsIn(ks, termOf(sides[0]!)).length);
  const solvedOne = eq.w.filter((w) => Math.abs(w) > 1e-12).length === 1;
  const one = solvedOne ? fam.cs[eq.w.findIndex((w) => Math.abs(w) > 1e-12)]! : null;
  const omove: OMove = { text, kind: 'ceq', w: eq.w, r: eq.r, solved };
  const twins = est.filter((e) => proportional(augmented(eq), augmented(e)));
  if (twins.length) {
    if (solved && !twins.some((e) => e.solved))
      return stepVerdict(
        base,
        'solveC',
        omove,
        false,
        one ? `Trouver ${one}` : STEP.solveC.label,
        one ? `On résout l’équation en ${one}.` : STEP.solveC.why,
      );
    return useless(base, 'Rien de nouveau', 'C’est la même équation, écrite autrement.');
  }
  const cond = ks.conds.find((c) => {
    const cv = condVec(ks, fam, c);
    return !!cv && proportional(augmented(eq), augmented(cv));
  });
  if (cond)
    return stepVerdict(
      base,
      'condition',
      omove,
      false,
      `Utiliser ${cond.text}`,
      `La condition ${cond.text} donne une équation en ${fam.cs.join(' et ')}.`,
    );
  const rows = est.map(augmented);
  if (rows.length && rank([...rows, augmented(eq)]) === rank(rows))
    return stepVerdict(
      base,
      'solveC',
      omove,
      false,
      one ? `Trouver ${one}` : 'Combiner les équations',
      one ? `On résout l’équation en ${one}.` : 'On combine les équations en les constantes.',
    );
  return jumpVerdict(
    base,
    omove,
    false,
    `Ta ligne est juste. Écris d’abord ce que donne ${ks.conds.map((c) => c.text).join(' et ')}, une étape par ligne, pour gagner des HORA. Tu peux la garder sans HORA.`,
  );
}

/* ── équation caractéristique, discriminant, racines ── */

function startQ(ks: KOdeState): [number, number, number] | null {
  if (ks.ode.order !== 2 || !ks.constCoef) return null;
  const [a0, a1, a2] = ks.ode.a.map((t) => val(t, {}));
  return [a2!, a1!, a0!];
}

function judgeChar(ks: KOdeState, base: Base, sides: Node[], text: string): KOdeVerdict {
  const q0 = startQ(ks);
  if (!q0)
    return useless(
      base,
      'Pas d’équation caractéristique ici',
      'L’équation caractéristique sert aux équations d’ordre 2 à coefficients constants.',
    );
  if (sides.length !== 2)
    return { kind: 'syntax', ...base, title: 'Une équation', message: 'Écris « r² + … = 0 ».' };
  const t = canon(add(termOf(sides[0]!), mul(num(-1), termOf(sides[1]!))));
  const q = quadOf(t, 'r');
  if (q && proportional(q, q0)) {
    if (ks.charQ)
      return useless(base, 'Rien de nouveau', 'Tu as déjà écrit l’équation caractéristique.');
    return stepVerdict(base, 'characteristic', { text, kind: 'char', q }, false);
  }
  return trapOr(ks, base, {
    title: 'Pas la bonne équation caractéristique',
    message: `À ${ks.fn}″ correspond r², à ${ks.fn}′ correspond r, à ${ks.fn} correspond 1 : on garde les coefficients.`,
  });
}

function judgeDelta(ks: KOdeState, base: Base, rest: Term[], text: string): KOdeVerdict {
  const q0 = startQ(ks);
  if (!q0)
    return useless(
      base,
      'Pas de discriminant ici',
      'Le discriminant sert à l’équation caractéristique (ordre 2).',
    );
  const qs = [q0, ...(ks.charQ ? [ks.charQ] : [])];
  const vals = rest.map((t) => (symbolsOf(t).size ? NaN : val(t, {})));
  const good = qs.find((q) => {
    const d = q[1] * q[1] - 4 * q[0] * q[2];
    return vals.every((v) => near(v, d));
  });
  if (!good)
    return trapOr(ks, base, {
      title: 'Pas le bon discriminant',
      message: 'Δ = b² − 4ac, avec les coefficients de l’équation caractéristique.',
    });
  const key = String(Math.round(vals[0]! * 1e9));
  const omove: OMove = { text, kind: 'delta', delta: vals[0]! };
  if (ks.deltas.has(key)) {
    if (rest.length === 1 && size(rest[0]!) <= 2)
      return useless(base, 'Rien de nouveau', 'Tu as déjà calculé Δ.');
    return useless(base, 'Rien de nouveau', 'Tu as déjà calculé Δ.');
  }
  return stepVerdict(base, 'discriminant', omove, false);
}

function judgeRoots(ks: KOdeState, base: Base, clauses: Node[][], text: string): KOdeVerdict {
  const q0 = startQ(ks);
  if (!q0)
    return useless(
      base,
      'Pas de racines ici',
      'Les racines de l’équation caractéristique servent aux équations d’ordre 2 à coefficients constants.',
    );
  const vals: Cx[] = [];
  for (const cl of clauses) {
    const h = cl[0]!;
    if (h.k !== 's' || h.n !== 'r' || cl.length !== 2)
      return {
        kind: 'syntax',
        ...base,
        title: 'Les racines',
        message: 'Écris « r = … ou r = … ».',
      };
    const c = cxOf(termOf(cl[1]!));
    if (!c)
      return {
        kind: 'syntax',
        ...base,
        title: 'Un nombre',
        message: 'Une racine est un nombre (réel ou complexe, avec i).',
      };
    vals.push(c);
  }
  const want = roots(q0);
  const allIn = vals.every((v) => want.some((w) => cxNear(v, w)));
  const covers = want.every((w) => vals.some((v) => cxNear(v, w)));
  if (!allIn)
    return trapOr(ks, base, {
      title: 'Pas les racines',
      message:
        'Vérifie : remplace r par ta valeur dans l’équation caractéristique, on doit trouver 0.',
    });
  if (!covers)
    return {
      kind: 'false',
      ...base,
      title: 'Il manque une racine',
      message:
        'L’équation caractéristique a deux racines : écris-les toutes les deux (« r = … ou r = … »).',
    };
  if (ks.rootsWritten) return useless(base, 'Rien de nouveau', 'Tu as déjà écrit les racines.');
  const label =
    want.length === 1
      ? 'Racine double'
      : want[0]!.im !== 0
        ? 'Racines complexes'
        : 'Deux racines réelles';
  return stepVerdict(base, 'roots', { text, kind: 'roots' }, false, label);
}

/* ------------------------------ mise à jour ------------------------------ */

export function kodeCommit(ks: KOdeState, v: KOdeVerdict): void {
  const m = v.omove;
  if (!m) return;
  ks.written.add(m.text);
  ks.lines++;
  switch (m.kind) {
    case 'eq':
      ks.eqs.push({ of: m.of!, shape: m.shape! });
      if (m.of === 'homog') ks.homogWritten = true;
      break;
    case 'family': {
      const cs = constsIn(ks, m.F!);
      ks.families.push({ of: m.of!, F: m.F!, cs, size: m.size! });
      if (m.of === 'full' && (!ks.cFamily || ks.cFamily.join() !== cs.join())) {
        ks.cFamily = cs;
        ks.cEqs = [];
        ks.cKnown = false;
      }
      break;
    }
    case 'one':
      ks.ones.push({ F: m.F!, size: m.size! });
      break;
    case 'deriv':
      ks.derivs.push({ order: m.order!, F: m.F!, size: m.size! });
      break;
    case 'eval':
      ks.evals.set(m.head!, Math.min(m.size!, ks.evals.get(m.head!) ?? Infinity));
      break;
    case 'value':
      ks.values.set(m.head!, Math.min(m.size!, ks.values.get(m.head!) ?? Infinity));
      break;
    case 'ceq': {
      const fam = refFamily(ks);
      if (fam && (!ks.cFamily || ks.cFamily.join() !== fam.cs.join())) {
        ks.cFamily = fam.cs;
        ks.cEqs = [];
      }
      ks.cEqs.push({ w: m.w!, r: m.r!, solved: !!m.solved });
      // les constantes sont fixées quand chacune a son équation « C = … » écrite
      if (fam)
        ks.cKnown = fam.cs.every((_, i) =>
          ks.cEqs.some(
            (e) =>
              e.solved &&
              Math.abs(e.w[i]!) > 1e-12 &&
              e.w.every((w, j) => j === i || Math.abs(w) < 1e-12),
          ),
        );
      break;
    }
    case 'char':
      ks.charQ = m.q!;
      break;
    case 'delta':
      ks.deltas.add(String(Math.round(m.delta! * 1e9)));
      break;
    case 'roots':
      ks.rootsWritten = true;
      break;
    default:
      break;
  }
  if (m.final) ks.concluded = true;
}

export function kodeGoalReached(ks: KOdeState): boolean {
  return ks.concluded;
}

/** une ligne (texte) lue : null si illisible */
function readText(ks: KOdeState, text: string): { base: Base; r: Read } | null {
  let A: string[];
  try {
    A = kodeLineAtoms(text, ks.fn, ks.x);
  } catch {
    return null;
  }
  return { base: { atoms: A, line: lineOf(A) }, r: readAtoms(A) };
}

/** la ligne est vraie (une réécriture, des solutions, une valeur juste) */
export function kodeHolds(ks: KOdeState, text: string): boolean {
  const rd = readText(ks, text);
  if (!rd || (rd.r.k !== 'eq' && rd.r.k !== 'or')) return false;
  const copy: KOdeState = { ...ks, written: new Set(), givenTexts: new Set(), traps: [] };
  let v: KOdeVerdict;
  try {
    v = judgeRead(copy, rd.base, rd.r);
  } catch {
    return false;
  }
  return v.kind === 'step' || (v.kind === 'useless' && v.title !== 'C’est l’énoncé' && !!v.omove);
}

/** la ligne est la réponse */
export function kodeIsAnswer(ks: KOdeState, text: string): boolean {
  const rd = readText(ks, text);
  if (!rd || rd.r.k !== 'eq' || rd.r.sides.length !== 2) return false;
  const sides = rd.r.sides;
  const h = sides[0]!;
  if (ks.goal === 'rewrite') {
    const o = lineOde(ks, sides);
    return !!o && sameOde(o, ks.ode, ks.x, ks.pts) && shapeOf(sides) === ks.answerShape;
  }
  if (ks.goal === 'value') {
    const tgt = ks.target!;
    const m = h.k === 's' ? headRe(ks.fn).exec(h.n!) : null;
    if (!m || m[1] !== '') return false;
    let V: Term;
    try {
      if (!near(val(exprOfText(m[2]!), {}), tgt.x0)) return false;
      V = termOf(sides[1]!);
    } catch {
      return false;
    }
    if (![...symbolsOf(V)].every((s) => s === 'π')) return false;
    return near(val(V, {}), tgt.v, Math.abs(tgt.v));
  }
  if (h.k !== 's' || h.n !== ks.fn) return false;
  let F: Term;
  try {
    F = termOf(sides[1]!);
  } catch {
    return false;
  }
  if ([...symbolsOf(F)].some((s) => ks.Y.includes(s)) || strangers(ks, F).length) return false;
  const c = classify(ks, F);
  if (ks.goal === 'general') return c.k === 'general' && c.of === 'full';
  if (c.k !== 'one' || c.of !== 'full') return false;
  return ks.goal === 'particular' || condsHold(ks, F);
}

/**
 * La réponse (texte) est-elle écrite sur la feuille ? Toutes les solutions, l'équation
 * réécrite : la feuille est conclue. Une solution : la même fonction est écrite (un QCM à
 * deux bonnes réponses, « y = 0 » et « y = −7e^(−3x) », ne coche que celle de l'élève).
 */
export function kodeAnswerWritten(ks: KOdeState, text: string): boolean {
  if (ks.goal === 'rewrite' || ks.goal === 'general' || ks.goal === 'value') return ks.concluded;
  const rd = readText(ks, text);
  if (!rd || rd.r.k !== 'eq' || rd.r.sides.length !== 2) return false;
  let F: Term;
  try {
    F = termOf(rd.r.sides[1]!);
  } catch {
    return false;
  }
  return ks.ones.some((o) => near(fnDistance(ks, o.F, F), 0));
}

/** une ligne déjà établie (le coup de pouce passe à la suivante) */
export function kodeEstablished(ks: KOdeState, text: string): boolean {
  let A: string[];
  try {
    A = kodeLineAtoms(text, ks.fn, ks.x);
  } catch {
    return false;
  }
  return ks.written.has(kodeText(A));
}

const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));

/** coup de pouce : la ligne du corrigé qui suit la dernière déjà écrite */
export function kodeNextMove(
  ks: KOdeState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (ks.concluded) return null;
  let from = 0;
  ks.path.forEach((t, i) => {
    if (kodeEstablished(ks, t)) from = i + 1;
  });
  for (let i = from; i < ks.path.length; i++) {
    const t = ks.path[i]!;
    if (kodeEstablished(ks, t)) continue;
    const atoms = kodeLineAtoms(t, ks.fn, ks.x);
    const v = kodeJudge(ks, chipsOf(atoms), { verify: true });
    return { line: lineOf(atoms), text: kodeText(atoms), atoms, label: v.rule?.label ?? '' };
  }
  return null;
}

export function kodeSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; atoms: string[]; v: KOdeVerdict }[];
  reached: boolean;
} {
  const ks = createKOdeState(model);
  const lines: { line: Line; text: string; atoms: string[]; v: KOdeVerdict }[] = [];
  for (const t of ks.path) {
    const atoms = kodeLineAtoms(t, ks.fn, ks.x);
    const v = kodeJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step') break;
    kodeCommit(ks, v);
    lines.push({ line: lineOf(atoms), text: kodeText(atoms), atoms, v });
  }
  return { lines, reached: ks.concluded };
}

/* ------------------------------ palette ------------------------------ */

/** les opérations des équations différentielles, dans l'ordre d'affichage */
export const ODE_OPS = [
  '+',
  '−',
  '×',
  '÷',
  '(',
  ')',
  '²',
  '^',
  '√',
  'exp',
  'ln',
  'cos',
  'sin',
  'π',
  'ou',
  '=',
];

/**
 * Les bouts (D6 : l'énoncé et le corrigé, jamais les pièges ni les choix) : y, y′, y″, x,
 * les constantes, r, Δ et i s'il y a une équation caractéristique, y(0)… des conditions,
 * les nombres, les opérations utiles.
 */
export function kodePaletteParts(model: ScratchModel): {
  names: string[];
  numbers: string[];
  ops: string[];
} {
  const spec = model.kernel!;
  const fn = spec.goal.target;
  const x = odeVariable(model);
  const texts = [spec.start, ...(spec.ode?.initial ?? []), ...(spec.path ?? []), spec.answer];
  const atoms = texts.flatMap((t) => {
    try {
      return kodeLineAtoms(t, fn, x);
    } catch {
      return [];
    }
  });
  const used = new Set(atoms);
  const consts = (spec.ode?.constants ?? ['C', 'A', 'B', 'C₁', 'C₂', 'K']).filter((c) =>
    used.has(c),
  );
  const heads = [...new Set(atoms.filter((a) => headRe(fn).test(a)))];
  const names = [
    fn,
    `${fn}′`,
    ...(used.has(`${fn}″`) ? [`${fn}″`] : []),
    x,
    ...consts,
    ...heads,
    ...['r', 'Δ', 'i'].filter((n) => used.has(n)),
    ...(used.has('e') ? ['e'] : []),
  ];
  const nums = [...new Set(atoms.filter((a) => isNum(a)))];
  const value = (s: string) => Number(s.replace(',', '.'));
  nums.sort((a, b) => value(a) - value(b) || a.localeCompare(b));
  const always = new Set(['+', '−', '×', '÷', '(', ')', '=']);
  const ops = ODE_OPS.filter((o) => always.has(o) || used.has(o));
  const sups = [...new Set(atoms.filter((a) => /^[⁰¹³⁴⁵⁶⁷⁸⁹]+$/.test(a)))];
  if (sups.length)
    ops.splice(ops.indexOf('²') >= 0 ? ops.indexOf('²') + 1 : ops.indexOf('='), 0, ...sups);
  return { names, numbers: nums, ops };
}

/** l'expression d'une ligne en texte (débogage, messages) */
export function kodeTermText(t: Term): string {
  return termText(t);
}
